import React, { useContext, useState } from 'react';
import clonedeep from 'lodash.clonedeep'
import { HttpMethods, FetchContext, NavContext, ApiRoutes, ToastContext, UserContext, InsightContext, NavRoutes } from '../../contexts';
import { useAggregatedState, RequestStatus, ApplicationRoles, SapTableNames, LocalTableNames } from '../../../helpers';
import { defaultCustomerRequest } from '../../../content';
import { DbType } from '../../../helpers/Common';
import { getTable } from '../../../helpers/Utils';

export const CustomerRequestContext = React.createContext(null);

export const CostumerRequestProvider = (props) => {        
    const { fetchAuthorized } = useContext(FetchContext);
    const { showSuccessMessage, showWarningMessage, showErrorMessage } = useContext(ToastContext);
    const { trackError, trackEvent } = useContext(InsightContext)

    const [state, setState] = useAggregatedState({
        tables: [],
        infoTypes: [],
        segments: [],

        // This data depends from the owner of the request
        salesGroups: [],
        userBusinessCategories: [],
        userCompanies: [],
        
        // This data depeneds from other fields
        respAreas: [],
        userSalesOrgs: [],
        salesOffices: [],
        customerGroups6: [],
        customerGroups7: [],
        customerGroups8: [],
        customerGroups9: [],
        customerGroups10: [],
        distributionChannels: [],
        custGroupsWithAccGroupS4: [],
        custGroupsWithAccGroupECC: [],
        recoAccounts: [],
        accClerks: [],
        deliveringPlants: [],
        taxCategories:[],

        requestDbType: null,
        request: null, 
        requestObsoleteSavedData: null,
        errors: [],
        submitAction: null,
        isBankInfoRequired: true,
        hasAccountGroupDIST: false,
        noSendToSap: false,
        logisticUsers: [],
        loadingLogisticUsers: false
    });

    const {
        userInfo,
        isUserInRole
    } = useContext(UserContext);

    const {
        navigateTo
    } = useContext(NavContext);

    const createEmptyRequest = async (selectedCompany, openerInfo) => {
        const request = {
            ...defaultCustomerRequest,
            customerRequestId: 0,
            status: RequestStatus.Draft,
            dbType: selectedCompany.dbType
        };

        request.hasVatNumber = true;
        request.companyId = selectedCompany.tableValueId;

        request.ownerKey = openerInfo ?
            openerInfo.externalKey :
            isUserInRole(ApplicationRoles.AOFOpener) ?
            userInfo.externalKey :
            null;

        request.ownerFullName = openerInfo ?
            openerInfo.displayName :
            isUserInRole(ApplicationRoles.AOFOpener) ?
            userInfo.displayName :
            null;

        setState({ request });
    }

    const resetRequestOwner = (ownerKey, ownerFullName) => {
        const request = { ...state.request };
        request.ownerKey = ownerKey;
        request.ownerFullName = ownerFullName;
        request.salesGroupId = "";
        request.responsibilityAreaId = "";
        request.businessCategoryId = "";
        request.companyId = "";
        request.salesOrganizationId = "";
        request.salesOfficeId = "";
        request.customerGroupId = "";
        request.customerGroup6Id = "";
        request.customerGroup7Id = "";
        request.customerGroup8Id = "";
        request.customerGroup9Id = "";
        request.customerGroup10Id = "";
        request.distributionChannelId = "";

        setState({ 
            request,
            salesGroups: [],
            userBusinessCategories: [],
            userCompanies: [],
            respAreas: [],
            userSalesOrgs: [],
            salesOffices: [],
            customerGroups6: [],
            customerGroups7: [],
            customerGroups8: [],
            customerGroups9: [],
            customerGroups10: [],
            distributionChannels: [],
            custGroupsWithAccGroupS4: [],
            custGroupsWithAccGroupECC: []
        });
    }

    const setIsBankInfoRequired = (isBankInfoRequired) => {
        setState({isBankInfoRequired});
    }

    const setHasAccountGroupDIST = (hasAccountGroupDIST) => {
        setState({hasAccountGroupDIST});
    }

    const setNoSendToSap = (noSendToSap) => {
        setState({noSendToSap});
    }

    const handleError = (error) => {
        try {
            const fieldErrors = JSON.parse(error.stack);
            return fieldErrors;
        }
        catch {
            return;
        }
    };

    const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
    const getUserCompanies = async (userKey?) => {
        try {
            setIsLoadingCompanies(true);

            const response = await fetchAuthorized(
                ApiRoutes.Request.GetUserCompanies({ userKey }),
                { method: HttpMethods.GET }
            );

            const userCompanies = await response.json();

            setState({ userCompanies });
            setIsLoadingCompanies(false);
        }
        catch(error) {
            console.error(error)
            trackError(error);
            setIsLoadingCompanies(false);
        }
    };

    const getUserCompanyOptions = (savedCompany) => {
        if (state.userCompanies.length > 0){
            if (!!savedCompany && !state.userCompanies.some(x => x.tableValueId === savedCompany.tableValueId)){
                const actualCompanyWithOldOne = [...state.userCompanies].concat(savedCompany);
                return actualCompanyWithOldOne.map(x => ({ value: x.tableValueId, label: `${x.externalKey} - ${x.value}` }));
            }

            return state.userCompanies.map(x => ({ value: x.tableValueId, label: `${x.externalKey} - ${x.value}` }));
        }
        else if (savedCompany)
            return [savedCompany].map(x => ({value: x.tableValueId, label: `${x.externalKey} - ${x.value}`}));
        else
            return [{ value: "", label: "No available values" }];
    };

    const [isLoadingRequest, setIsLoadingRequest] = useState(false);
    const loadRequestDbType = async (customerRequestId) => {
        try {
            setIsLoadingRequest(true);

            const response = await fetchAuthorized(
                ApiRoutes.Request.GetCustomerRequestDbType({ customerRequestId }),
                { method: HttpMethods.GET }
            );
            
            const requestDbType = await response.json();
            setState({ requestDbType });
        }
        catch(e) {
            console.error(e);
            trackError(e);
            setIsLoadingRequest(false);

            if (e.code === 403)
                navigateTo(NavRoutes.Forbidden);
        }
    };

    const loadRequest = async (customerRequestId, dbType) => {
        try {
            let routeBasedOnDbType: string;

            if (dbType === DbType.ECC)
                routeBasedOnDbType = ApiRoutes.Request.GetCustomerRequestECC({ customerRequestId });
            else
                routeBasedOnDbType = ApiRoutes.Request.GetCustomerRequestS4({ customerRequestId })

            const response = await fetchAuthorized(
                routeBasedOnDbType,
                { method: HttpMethods.GET }
            );
            
            const request = await response.json();

            setState({ request });
        }
        catch(e) {
            console.error(e);
            trackError(e);

            if (e.code === 403)
                navigateTo(NavRoutes.Forbidden);
        }
        finally {
            setIsLoadingRequest(false);
        }
    };

    const getRequestSavedObsoleteDataECC = async (customerRequestId, dbType) => {
        try {
            let routeBasedOnDbType: string;

            if (dbType === DbType.ECC)
                routeBasedOnDbType = ApiRoutes.Request.GetRequestSavedObsoleteDataECC({ customerRequestId });

            const response = await fetchAuthorized(
                routeBasedOnDbType,
                { method: HttpMethods.GET }
            );
            
            const requestObsoleteSavedData = await response.json();
            setState({ requestObsoleteSavedData });
        }
        catch(e) {
            console.error(e);
            trackError(e);

            if (e.code === 403)
                navigateTo(NavRoutes.Forbidden);
        }
        finally {
            setIsLoadingRequest(false);
        }
    };

    const [isSavingRequest, setIsSavingRequest] = useState(false);
    const saveDraftRequest = async (data, send = false) => {
        try {
            setIsSavingRequest(true);

            const route = data.dbType === DbType.ECC ?
            (
                send ?
                ApiRoutes.Request.SendCustomerRequestDraftECC() :
                ApiRoutes.Request.SaveCustomerRequestDraftECC()
            ) :
            (
                send ?
                ApiRoutes.Request.SendCustomerRequestDraftS4() :
                ApiRoutes.Request.SaveCustomerRequestDraftS4()
            );

            const r = await fetchAuthorized(
                route,
                {
                    method: HttpMethods.POST,
                    body: JSON.stringify(data),
                    collectErrorDetails: true
                }
            );

            if (send)
                trackEvent("SendCustomerRequestDraft", data);
            else
                trackEvent("SaveCustomerRequestDraft", data);
            
            let request = await r.json();
            request = { ...data, ...request };
            setState({ request });

            if (send)
                showSuccessMessage("The customer request has been sent for approval");
            else
                showSuccessMessage("A draft copy of the customer request has been saved");
            
            setState({ errors: [] });
            setIsSavingRequest(false);
            return true;
            
        } catch(e) {
            console.error(e);
            trackError(e);
            setState({ errors: e.stack && e.stack.length > 0 ? handleError(e) : []});
            setIsSavingRequest(false);
            return false;
        }
    }

    const updateCreditRiskInfo = async (data, send = false) => {
        try {
            setIsSavingRequest(true);
            
            const handledCreditManagement = { ...data.creditManagement };
            handledCreditManagement.creditTypes.map(x => {
                if (!x.import || x.import < 0)
                    x.import = 0;
                
                return x;
            });

            const route = state.requestDbType === DbType.ECC ?
            (
                send ?
                ApiRoutes.Request.SendCreditRiskInfoECC() :
                ApiRoutes.Request.UpdateCreditRiskInfoECC()
            ) :
            (
                send ?
                ApiRoutes.Request.SendCreditRiskInfoS4() :
                ApiRoutes.Request.UpdateCreditRiskInfoS4()
            );

            const resolve = await fetchAuthorized(
                route,
                {
                    method: HttpMethods.POST,
                    body: JSON.stringify(data),
                    collectErrorDetails: true
                }
            );

            trackEvent("updateCreditRiskInfo", data)
            
            let request = await resolve.json();

            request = {
                ...state.request,
                ...request,
                order: {
                    ...state.request.order,
                    paymentTermId: data.paymentTermId
                },
                riskEvaluation: {
                    ...data.riskEvaluation
                },
                ethicsCompliance: {
                    ...data.ethicsCompliance
                },
                creditManagement: handledCreditManagement
            };

            setState({ request });

            if (send)
                showSuccessMessage("The customer request has been sent for approval");
            else
                showSuccessMessage("Changes to the customer request has been saved");
            
            setState({ errors: []});
            setIsSavingRequest(false);
            return true;
        }
        catch(e) {
            console.error(e);
            setState({ errors: e.stack && e.stack.length > 0 ? handleError(e) : [] });
            setIsSavingRequest(false);
            return false;
        }
    };

    const blockRequest = async (data) => {
        try {        
            setIsSavingRequest(true);

            const handledCreditManagement = { ...data.creditManagement };
            handledCreditManagement.creditTypes.map(x => {
                if (!x.import || x.import < 0)
                    x.import = 0;
                
                return x;
            });

            const route = state.requestDbType === DbType.ECC ?
                ApiRoutes.Request.BlockRequestECC() :
                ApiRoutes.Request.BlockRequestS4();

            const resolve = await fetchAuthorized(
                route,
                {
                    method: HttpMethods.POST,
                    body: JSON.stringify(data),
                    collectErrorDetails: true
                }
            );

            trackEvent("blockRequest", data);
            
            let request = await resolve.json();

            request = {
                ...state.request,
                ...request,
                order: {
                    ...state.request.order,
                    paymentTermId: data.paymentTermId
                },
                riskEvaluation: {
                    ...data.riskEvaluation
                },
                ethicsCompliance: {
                    ...data.ethicsCompliance
                },
                creditManagement: handledCreditManagement
            };

            setState({ request });
            showSuccessMessage("The customer request has been blocked");
            setState({ errors: []});
            setIsSavingRequest(false);

            return true;
        }
        catch(e) {
            console.error(e);
            setState({ errors: e.stack && e.stack.length > 0 ? handleError(e) : []});
            setIsSavingRequest(false);
            return false;
        }
    }

    const unblockRequest = async (data) => {
        try {                
            setIsSavingRequest(true);

            const r = await fetchAuthorized(
                ApiRoutes.Request.UnblockRequest(),
                {
                    method: HttpMethods.POST,
                    body: JSON.stringify(data),
                    collectErrorDetails: true
                }
            );
            
            trackEvent("UnblockRequest", data);

            let request = await r.json();
            request = { ...state.request, ...request };

            setState({ request });
            showSuccessMessage("The request has been unblocked and sent back to credit risk");            
            setState({ errors: []});
            setIsSavingRequest(false);

            return true;
        }
        catch(e) {
            console.error(e);
            trackError(e);
            setState({ errors: e.stack && e.stack.length > 0 ? handleError(e) : []});
            setIsSavingRequest(false);
            return false;
        }
    }

    const closeRequest = async (data) => {
        try {                
            setIsSavingRequest(true);

            const response = await fetchAuthorized(
                ApiRoutes.Request.CloseRequest(),
                {
                    method: HttpMethods.POST,
                    body: JSON.stringify(data),
                    collectErrorDetails: true
                }
            );
            
            trackEvent("CloseRequest", data);

            let request = await response.json();
            request = { ...state.request, ...request };

            setState({ request });
            showSuccessMessage("The request has been closed");            
            setState({ errors: []});

            return true;
        }
        catch(e) {
            console.error(e);
            trackError(e);
            setState({ errors: e.stack && e.stack.length > 0 ? handleError(e) : []});
            return false;
        }
        finally {
            setIsSavingRequest(false);
        }
    };

    const updateSignedContractInfo = async (data, send = false) => {
        try {
            setIsSavingRequest(true);

            const route = state.requestDbType === DbType.ECC ?
            (
                send ?
                ApiRoutes.Request.SendSignedContractECC() :
                ApiRoutes.Request.UpdateSignedContractECC()
            ) :
            (
                send ?
                ApiRoutes.Request.SendSignedContractS4() :
                ApiRoutes.Request.UpdateSignedContractS4()
            );

            const resolve = await fetchAuthorized(
                route,
                {
                    method: HttpMethods.POST,
                    body: JSON.stringify(data),
                    collectErrorDetails: true
                });

            if (send)
                trackEvent("SendSignedContract", data);
            else
                trackEvent("UpdateSignedContract", data);
            
            let request = await resolve.json();

            request = { 
                ...state.request, 
                ...request, 
                isSignedContractReady: send,
                isSignedContractAvailable: data.isSignedContractAvailable, 
                signedContract: data.signedContract ? { ...data.signedContract } : null
            };
            
            setState({ request });

            if(send)
                showSuccessMessage("The customer request has been sent for approval");
            else
                showSuccessMessage("Changes to the customer request has been saved");
            
            setState({ errors: []});
            setIsSavingRequest(false);
            return true;
        }
        catch(e) {
            console.error(e);
            setState({ errors: e.stack && e.stack.length > 0 ? handleError(e) : []});
            setIsSavingRequest(false);
            return false;
        }
    };

    const updateLogisticDocInfo = async (data, send = false) => {
        try {
            setIsSavingRequest(true);

            const route = state.requestDbType === DbType.ECC ?
            (
                send ?
                ApiRoutes.Request.SendLogisticDocInfoECC() :
                ApiRoutes.Request.UpdateLogisticDocInfoECC()
            ) :
            (
                send ?
                ApiRoutes.Request.SendLogisticDocInfoS4() :
                ApiRoutes.Request.UpdateLogisticDocInfoS4()
            );

            const r = await fetchAuthorized(
                route,
                {
                    method: HttpMethods.POST,
                    body: JSON.stringify(data),
                    collectErrorDetails: true
                }
            );

            if (send)
                trackEvent("SendLogisticDocInfo", data);
            else
                trackEvent("UpdateLogisticDocInfo", data);
            
            let request = await r.json();

            request = { 
                ...state.request, 
                ...request, 
                isLogisticDocumentationReady: send, 
                logisticDocumentation: data.logisticDocumentation ? { ...data.logisticDocumentation } : null
            };       

            setState({ request });

            if (send)
                showSuccessMessage("The customer request has been sent for approval");
            else
                showSuccessMessage("Changes to the customer request has been saved");
            
            setState({ errors: []});
            setIsSavingRequest(false);
            return true;
        }
        catch(e) {
            console.error(e);
            setState({ errors: e.stack && e.stack.length > 0 ? handleError(e) : []});
            setIsSavingRequest(false);
            return false;
        }
    };

    const updateLogisticCuSrvInfo = async (data, send = false) => {
        try {
            setIsSavingRequest(true);

            const route = state.requestDbType === DbType.ECC ?
            (
                send ?
                ApiRoutes.Request.SendLogisticCuSrvInfoECC() :
                ApiRoutes.Request.UpdateLogisticCuSrvInfoECC()
            ) :
            (
                send ?
                ApiRoutes.Request.SendLogisticCuSrvInfoS4() :
                ApiRoutes.Request.UpdateLogisticCuSrvInfoS4()
            );

            const response = await fetchAuthorized(
                route,
                {
                    method: HttpMethods.POST,
                    body: JSON.stringify(data),
                    collectErrorDetails: true
                });

            if (send)
                trackEvent("SendLogisticCuSrvInfo", data);
            else
                trackEvent("UpdateLogisticCuSrvInfo", data);
            
            let request = await response.json();

            request = { 
                ...state.request, 
                ...request, 
                isLogisticCustomerServiceReady: true, 
                logisticCustomerService: data.logisticCustomerService ? { ...data.logisticCustomerService } : null
            };

            setState({ request });

            if (send)
                showSuccessMessage("The customer request has been sent for approval");
            else
                showSuccessMessage("Changes to the customer request has been saved");
            
            setState({ errors: []});
            setIsSavingRequest(false);
            return true;
        }
        catch(e) {
            console.error(e);
            setState({ errors: e.stack && e.stack.length > 0 ? handleError(e) : []});
            setIsSavingRequest(false);
            return false;
        }
    };

    const updateLogisticPlanInfo = async (data, send = false) => {
        try {
            setIsSavingRequest(true);

            const route = state.requestDbType === DbType.ECC ?
            (
                send ?
                ApiRoutes.Request.SendLogisticPlanInfoECC() :
                ApiRoutes.Request.UpdateLogisticPlanInfoECC()
            ) :
            (
                send ?
                ApiRoutes.Request.SendLogisticPlanInfoS4() :
                ApiRoutes.Request.UpdateLogisticPlanInfoS4()
            );

            const r = await fetchAuthorized(
                route,
                {
                    method: HttpMethods.POST,
                    body: JSON.stringify(data),
                    collectErrorDetails: true
                });

            if(send){
                trackEvent("SendLogisticPlanInfo", data)
            } else {
                trackEvent("UpdateLogisticPlanInfo", data)
            }
            
            let request = await r.json();
            request = { 
                ...state.request, 
                ...request, 
                isLogisticPlanningReady: send, 
                logisticPlanning: data.logisticPlanning ? { ...data.logisticPlanning } : null
            };
            setState({ request });

            if(send) {
                showSuccessMessage("The customer request has been sent for approval");
            } else {
                showSuccessMessage("Changes to the customer request has been saved");                
            }
            
            setState({ errors: []});
            setIsSavingRequest(false);
            return true;

        } catch(e) {
            console.error(e);
            setState({ errors: e.stack && e.stack.length > 0 ? handleError(e) : []});
            setIsSavingRequest(false);
            return false;
        }
    }

    const updateLogisticInfo = async (data, send = false) => {
        try {
            setIsSavingRequest(true);

            const apis: string[] = state.requestDbType === DbType.ECC ?
            (
                send ?
                [
                    ApiRoutes.Request.SendLogisticDocInfoECC(),
                    ApiRoutes.Request.SendLogisticCuSrvInfoECC(),
                    ApiRoutes.Request.SendLogisticPlanInfoECC()
                ] :
                [
                    ApiRoutes.Request.UpdateLogisticDocInfoECC(),
                    ApiRoutes.Request.UpdateLogisticCuSrvInfoECC(),
                    ApiRoutes.Request.UpdateLogisticPlanInfoECC()
                ]
            ) :
            (
                send ?
                [
                    ApiRoutes.Request.SendLogisticDocInfoS4(),
                    ApiRoutes.Request.SendLogisticCuSrvInfoS4(),
                    ApiRoutes.Request.SendLogisticPlanInfoS4()
                ] :
                [
                    ApiRoutes.Request.UpdateLogisticDocInfoS4(),
                    ApiRoutes.Request.UpdateLogisticCuSrvInfoS4(),
                    ApiRoutes.Request.UpdateLogisticPlanInfoS4()
                ]
            );

            const eventDescriptions = state.requestDbType === DbType.ECC ?
                [
                    send ? "SendLogisticDocInfoECC" : "UpdateLogisticDocInfoECC",
                    send ? "SendLogisticCuSrvInfoECC" : "UpdateLogisticCuSrvInfoECC",
                    send ? "SendLogisticPlanInfoECC" : "UpdateLogisticPlanInfoECC"
                ] :
                [
                    send ? "SendLogisticDocInfoS4" : "UpdateLogisticDocInfoS4",
                    send ? "SendLogisticCuSrvInfoS4" : "UpdateLogisticCuSrvInfoS4",
                    send ? "SendLogisticPlanInfoS4" : "UpdateLogisticPlanInfoS4",
                ];

            let r = null;
            let request = { ...state.request };
            let errors = [];
            let error = null;

            for(let i = 0; i < data.length; i++) {
                try {
                    r = await fetchAuthorized(
                        apis[data[i].type],
                        {
                            method: HttpMethods.POST,
                            body: JSON.stringify(data[i]),
                            collectErrorDetails: true,
                            silent: true
                        }
                    );
                    
                    trackEvent(eventDescriptions[data[i].type], data[i])
                }
                catch(e) {
                    trackError(e);

                    if (e.stack && e.stack.length) {
                        errors = errors.concat(handleError(e));
                        continue;               
                    }
                    else
                        error = e.message;
                }
                
                if (!error) {
                    let update = await r.json();
                    
                    request = { 
                        ...request, 
                        ...update
                    };

                    switch(data[i].type) {
                        // Logistic Documentation
                        case 0:
                            request = {                        
                                ...request,
                                isLogisticDocumentationReady: send, 
                                logisticDocumentation: data[i].logisticDocumentation ? { ...data[i].logisticDocumentation } : null
                            }
                            break;
                        // Logistic Customer Service
                        case 1:
                            request = {                        
                                ...request,
                                isLogisticCustomerServiceReady: send, 
                                logisticDocumentation: request.isPerfumesCategory ? { transportationZoneId: data[i].transportationZoneId } : request.logisticDocumentation,
                                logisticCustomerService: data[i].logisticCustomerService ? { ...data[i].logisticCustomerService } : null
                            }
                            break;
                        // Distribution Planning
                        case 2:
                            request = {                        
                                ...request,
                                isLogisticPlanningReady: send, 
                                logisticPlanning: data[i].logisticPlanning ? { ...data[i].logisticPlanning } : null
                            }
        
                            break;

                        default:
                            break;
                    }           
                }        
            }

            setState({ request });

            if(!error && errors.length === 0) {                
                if(send) {
                    showSuccessMessage("The customer request has been sent for approval");
                } else {
                    showSuccessMessage("Changes to the customer request has been saved");                
                }
                setState({ errors: []});
            } else if(error) {
                showErrorMessage(error);
            } else {
                showErrorMessage("Invalid data supplied");
                setState({ errors });
            }
            
            setIsSavingRequest(false);
            return !error && errors.length === 0;

        } catch(e) {
            console.error(e);  
            trackError(e);                
            setIsSavingRequest(false);
            return false;
        }
    }

    const updateAccountReceivableInfo = async (data, send) => {
        try {
            setIsSavingRequest(true);

            const route = state.requestDbType === DbType.ECC ?
            (
                send ?
                ApiRoutes.Request.SendAccountReceivableInfoECC() :
                ApiRoutes.Request.UpdateAccountReceivableInfoECC()
            ) :
            (
                send ?
                ApiRoutes.Request.SendAccountReceivableInfoS4() :
                ApiRoutes.Request.UpdateAccountReceivableInfoS4()
            );

            const response = await fetchAuthorized(
                route,
                {
                    method: HttpMethods.POST,
                    body: JSON.stringify(data),
                    collectErrorDetails: true
                }
            );

            if (send)
                trackEvent("SendAccountReceivableInfo", data);
            else
                trackEvent("UpdateAccountReceivableInfo", data);
            
            let request = await response.json();

            request = { ...data, ...request };
            setState({ request });

            let success = true;

            if (send) {
                if (!request.hasChanges) {
                    if (request.status == RequestStatus.WaitingToBeAddedInSap)
                        showSuccessMessage("The customer request has been queued to be added in sap");
                    else
                        showSuccessMessage("The customer request has been approved");
                }
                else {
                    showWarningMessage("The customer request has changed and cannot be approved");
                    success = false;
                }
            }
            else
                showSuccessMessage("Changes to the customer request has been saved");

            setState({ errors: []});
            setIsSavingRequest(false);
            return success;
        }
        catch(e) {
            console.error(e);
            trackError(e);
            setState({ errors: e.stack && e.stack.length > 0 ? handleError(e) : []});
            setIsSavingRequest(false);
            return false;
        }
    };

    const sendRequestToCreditRisk = async (data) => {
        try {
            setIsSavingRequest(true);

            const response = await fetchAuthorized(
                ApiRoutes.Request.SendCustomerRequestToCreditRisk(),
                {
                    method: HttpMethods.POST,
                    body: JSON.stringify(data),
                    collectErrorDetails: true
                }
            );
            
            trackEvent("SendCustomerRequestToCreditRisk", data)

            let request = await response.json();
            request = { ...state.request, ...request, };
            setState({ request });

            showSuccessMessage("The customer request has been sent to credit risk");            

            setState({ errors: []});
            return true;

        }
        catch(e) {
            console.error(e);
            setState({ errors: e.stack && e.stack.length > 0 ? handleError(e) : []});
            return false;
        }
        finally {
            setIsSavingRequest(false);
        }
    };

    const sendRequestToLogistic = async (data) => {
        try {
            setIsSavingRequest(true);

            const response = await fetchAuthorized(
                ApiRoutes.Request.SendCustomerRequestToLogistic(),
                {
                    method: HttpMethods.POST,
                    body: JSON.stringify(data),
                    collectErrorDetails: true
                }
            );
            
            trackEvent("SendCustomerRequestToLogistic", data);

            let request = await response.json();
            request = { ...state.request, ...request };

            setState({ request });
            showSuccessMessage("The customer request has been sent to logistic");            
            setState({ errors: []});

            return true;
        }
        catch(e) {
            console.error(e);
            trackError(e);
            setState({ errors: e.stack && e.stack.length > 0 ? handleError(e) : []});
            return false;
        }
        finally {
            setIsSavingRequest(false);
        }
    }

    const updatePricingInfo = async (data, send = false) => {
        try {
            setIsSavingRequest(true);
            
            const response = await fetchAuthorized(
                send ? ApiRoutes.Request.SendCustomerRequestToARFromPricing() : ApiRoutes.Request.UpdatePricingInfo(),
                {
                    method: HttpMethods.POST,
                    body: JSON.stringify(data),
                    collectErrorDetails: true
                }
            );
            
            trackEvent("UpdatePricingInfo", data);

            let request = await response.json();

            request = {
                ...state.request,
                ...request,
                pricing: {
                    pricingListId: data.pricingListId,
                    pricingGroupId: data.pricingGroupId
                }
            };

            if (send)
                showSuccessMessage("The customer request has been sent to AR From Pricing");
            else
                showSuccessMessage("Changes to the customer request has been saved");

            setState({ request });
            setState({ errors: []});
            setIsSavingRequest(false);

            return true;
        }
        catch(e) {
            console.error(e);
            trackError(e);
            setState({ errors: e.stack && e.stack.length > 0 ? handleError(e) : []});
            setIsSavingRequest(false);
            return false;
        }
    }

    const sendRequestToCRE = async (data) => {
        try {
            setIsSavingRequest(true);

            const response = await fetchAuthorized(
                ApiRoutes.Request.SendCustomerRequestToCRE(),
                {
                    method: HttpMethods.POST,
                    body: JSON.stringify(data),
                    collectErrorDetails: true
                }
            );

            trackEvent("SendCustomerRequestToCRE", data)
            
            let request = await response.json();
            request = { ...state.request, ...request, };
            setState({ request });

            showSuccessMessage("The customer request has been sent to CRE");            

            setState({ errors: []});
            return true;

        }
        catch(e) {
            console.error(e);
            trackError(e);
            setState({ errors: e.stack && e.stack.length > 0 ? handleError(e) : []});
            return false;
        }
        finally {
            setIsSavingRequest(false);
        }
    };

    const sendRequestToARFromCR = async (data) => {
        try {
            setIsSavingRequest(true);

            const response = await fetchAuthorized(
                ApiRoutes.Request.SendCustomerRequestToARFromCR(),
                {
                    method: HttpMethods.POST,
                    body: JSON.stringify(data),
                    collectErrorDetails: true
                }
            );

            trackEvent("SendCustomerRequestToARFromCR", data)
            
            let request = await response.json();
            request = { ...state.request, ...request, };
            setState({ request });

            showSuccessMessage("The customer request has been sent to AR From CR");            

            setState({ errors: []});
            return true;
        }
        catch(e) {
            console.error(e);
            trackError(e);
            setState({ errors: e.stack && e.stack.length > 0 ? handleError(e) : []});
            return false;
        }
        finally {
            setIsSavingRequest(false);
        }
    };

    const sendRequestToReview = async (data) => {
        try {
            setIsSavingRequest(true);
            
            const response = await fetchAuthorized(
                ApiRoutes.Request.SendCustomerRequestToReview(),
                {
                    method: HttpMethods.POST,
                    body: JSON.stringify(data),
                    collectErrorDetails: true
                }
            );
            
            trackEvent("SendCustomerRequestToReview", data)

            let request = await response.json();
            request = { ...state.request, ...request, };
            setState({ request });

            showSuccessMessage("The customer request has been sent to review");            

            setState({ errors: []});
            return true;
        }
        catch(e) {
            console.error(e);
            trackError(e);
            setState({ errors: e.stack && e.stack.length > 0 ? handleError(e) : []});
            return false;
        }
        finally {
            setIsSavingRequest(false);
        }
    };

    const reSendRequestToSap = async (customerRequestId, dbType) => {
        try {
            setIsSavingRequest(true);

            const response = await fetchAuthorized(
                ApiRoutes.Request.ReSendCustomerRequestToSap({ customerRequestId, dbType }),
                { method: HttpMethods.POST }
            );
            
            trackEvent("ReSendRequestToSap")

            let request = await response.json();
            request = { ...state.request, ...request, };
            setState({ request });

            showSuccessMessage("The customer request has been sent to sap");            

            setState({ errors: []});
            return true;

        }
        catch(e) {
            console.error(e);
            trackError(e);
            return false;
        }
        finally {
            setIsSavingRequest(false);
        }
    };

    const rejectRequest = async (data) => {
        try {
            setIsSavingRequest(true);

            const response = await fetchAuthorized(
                ApiRoutes.Request.RejectCustomerRequest(),
                {
                    method: HttpMethods.POST,
                    body: JSON.stringify(data),
                    collectErrorDetails: true
                }
            );
            
            trackEvent("RejectCustomerRequest", data)

            let request = await response.json();
            request = { ...state.request, ...request, };
            setState({ request });

            showSuccessMessage("The customer request has been rejected");            

            setState({ errors: []});
            return true;

        }
        catch(e) {
            console.error(e);
            trackError(e);
            setState({ errors: e.stack && e.stack.length > 0 ? handleError(e) : []});
            return false;
        }
        finally {
            setIsSavingRequest(false);
        }
    };

    const [isLoadingDefs, setIsLoadingDefs] = useState(false);
    const getChoosenTableDefinitions = async (dbType) => {  
        try {
            const nameList = [
                SapTableNames.CG4,
                //SapTableNames.Companies,
                SapTableNames.Countries,
                SapTableNames.CreditControlAreas,
                SapTableNames.Currencies,
                //SapTableNames.CustomerGroups,
                SapTableNames.DistributionChannels,
                SapTableNames.CustomerTypes,
                SapTableNames.DeliveryPriorities,
                SapTableNames.Incoterms,
                SapTableNames.PricingGroups,
                SapTableNames.PricingLists,
                SapTableNames.RequestedDeliveryDateMandatory,
                //SapTableNames.ResponsibilityAreas,
                SapTableNames.SalesDistricts,
                //SapTableNames.SalesGroups,
                //SapTableNames.SalesOffices,
                SapTableNames.SalesOrganizations, //needed only for Segment field
                //SapTableNames.Segments,
                SapTableNames.ShippingConditions,
                SapTableNames.States,
                SapTableNames.TransportationZones,
                SapTableNames.PaymentTerms,
                SapTableNames.Divisions,
                //SapTableNames.AccountGroups,
                SapTableNames.AccountAssignmentGroup,
                //SapTableNames.AccountingClerks,
                SapTableNames.CashManagementGroups,
                SapTableNames.SortKeys,
                SapTableNames.Chains,
                SapTableNames.DunningProcedures,
                SapTableNames.InfoTypes,
                //LocalTableNames.BusinessCategories,
                LocalTableNames.PrincipalOperationCountries,
                LocalTableNames.Ratings
            ];

            setIsLoadingDefs(true);

            const response = await fetchAuthorized(
                ApiRoutes.Table.GetChoosenTableDefinitions({
                    nameList,
                    dbType,
                    withRelatedTable: false,
                    withValues: true,
                    isObsolete: false
                }),
                { method: HttpMethods.GET }
            );

            const tables = await response.json();
            setState({ tables });
        }
        catch(e) {
            console.error(e);
            trackError(e);
        }
        finally {
            setIsLoadingDefs(false);
        }
    };

    const getTableValues = (tableName, dbType, relatedValueId?) => {
        if (state.tables && state.tables.length > 0) {
            const table = getTable(tableName, state);

            if (
                table &&
                (
                    table.childValues_ECC ||
                    table.childValues_S4
                )
            ){
                const childValues = dbType === DbType.ECC ?
                    table.childValues_ECC :
                    table.childValues_S4;

                let values = [].concat(childValues);

                if (relatedValueId) {
                    values = values
                        .filter(x =>
                            x.relatedValueIds.find(id => id === relatedValueId) !== undefined
                        );

                    if (values.length === 0)
                        return [];
                }
                
                return values;
            }
            else
                return [];
        }
        else
            return [];
    };

    const getTableOptions = (tableName, dbType, relatedValueId, allowEmpty) => {
        const values = getTableValues(tableName, dbType, relatedValueId);

        if (values && values.length > 0){
            let options = values.map(x => ({ value: x.tableValueId, label: x.value }));

            if (allowEmpty)
                options = [ { value: "", label: "@None" }].concat(options);
            
            return options;
        }
        else
            return [{ value: "", label: "No available values" }];
    };

    const getTableOptionsWithCode = (tableName, dbType, relatedValueId, allowEmpty) => {
        const values = getTableValues(tableName, dbType, relatedValueId);
        
        if (values && values.length > 0){
            let options = values.map(x => ({
                value: x.tableValueId,
                label: `${x.externalKey} - ${x.value}`
            }));

            if (allowEmpty)
                options = [{ value: "", label: "@None" }].concat(options);

            return options;
        }
        else
            return [{ value: "", label: "No available values" }];
    }

    const getTableOptionsWithNumber = (tableName, dbType, relatedValueId, allowEmpty) => {
        const values = getTableValues(tableName, dbType, relatedValueId);
        if(values && values.length > 0) {
            const sortByNumberInExternalKey = (a, b) => {
                if (
                    Number(a.externalKey.match(/\d+/) &&
                    a.externalKey.match(/\d+/)[0]) < Number(b.externalKey.match(/\d+/) && b.externalKey.match(/\d+/)[0])
                )
                  return -1;
                  
                if (
                    Number(a.externalKey.match(/\d+/) && a.externalKey.match(/\d+/)[0]) > Number(b.externalKey.match(/\d+/) && b.externalKey.match(/\d+/)[0])
                )
                  return 1;
                  
                return 0;
            };
              
            values.sort(sortByNumberInExternalKey);

            let options = values.map(x => ({
                value: x.tableValueId,
                label: `${x.externalKey.match(/\d+/) && x.externalKey.match(/\d+/)[0]} - ${x.value}`
            }));

            if (allowEmpty)
                options = [{ value: "", label: "@None" }].concat(options);

            return options;
        }
        else
            return [{ value: "", label: "No available values" }];
    }

    const getStateOptions = (countryId, dbType) => {
        const values = getTableValues(SapTableNames.States, dbType, countryId);
        if(values && values.length > 0) {
            return values.map(x => ({ value: x.tableValueId, label: `${x.externalKey.split("_")[1]} - ${x.value}` }));
        } else {
            return [{ value: "", label: "No available values" }];
        }
    }

    const [isLoadingSalesGroup, setIsLoadingSalesGroup] = useState(false);
    const getSalesGroups = async (userKey, dbType) => {
        try {
            setIsLoadingSalesGroup(true);

            const response = await fetchAuthorized(
                ApiRoutes.Request.GetUserSalesGroups({ userKey, dbType }),
                { method: HttpMethods.GET }
            );

            const salesGroups = await response.json();
            setState({ salesGroups });
            setIsLoadingSalesGroup(false);
        }
        catch(e) {
            console.error(e);
            trackError(e);
            setIsLoadingSalesGroup(false); 
        }
    };

    const getSalesGroupOptions = () => {
        if(state.salesGroups.length > 0) {
            return state.salesGroups.map(x => ({ value: x.tableValueId, label: `${x.externalKey} - ${x.value}` }));
        } else {
            return [{ value: "", label: "No available values" }];
        }
    }

    const [isLoadingRespAreas, setIsLoadingRespAreas] = useState(false);
    const getRespAreas = async (salesGroupId, dbType) => {
        try {
            setIsLoadingRespAreas(true);
            const response = await fetchAuthorized(
                ApiRoutes.Request.GetRespAreas({ salesGroupId, dbType }),
                { method: HttpMethods.GET }
            );
            const respAreas = await response.json();
            setState({ respAreas });
            setIsLoadingRespAreas(false);
        }
        catch(e) {
            console.error(e);
            trackError(e);
            setIsLoadingRespAreas(false);
        }
    };

    const getRespAreasOptions = () => {
        if(state.respAreas.length > 0) {
            return state.respAreas.map(x => ({ value: x.tableValueId, label: `${x.externalKey} - ${x.value}` }));
        } else {
            return [{ value: "", label: "No available values" }];
        }        
    }

    const [isLoadingSalesOffices, setIsLoadingSalesOffices] = useState(false);
    const getSalesOffices = async (salesGroupId, salesOrgId, dbType) => {
        try {
            setIsLoadingSalesOffices(true);

            const response = await fetchAuthorized(
                ApiRoutes.Request.GetSalesOffices({ salesGroupId, salesOrgId, dbType }),
                { method: HttpMethods.GET }
            );
            const salesOffices = await response.json();

            setState({ salesOffices });
        }
        catch(e) {
            console.error(e);
            trackError(e);
        }
        finally {
            setIsLoadingSalesOffices(false);
        }
    };

    const getSalesOfficeOptions = () => {
        if(state.salesOffices.length > 0) {
            return state.salesOffices.map(x => ({ value: x.tableValueId, label: `${x.externalKey} - ${x.value}` }));
        } else {
            return [{ value: "", label: "No available values" }];
        }        
    };

    const [isLoadingSalesOrgs, setIsLoadingSalesOrgs] = useState(false);
    const getSalesOrgsAsync = async (companyId, dbType) => {
        if (companyId){
            try {
                setIsLoadingSalesOrgs(true);

                const response = await fetchAuthorized(
                    ApiRoutes.Request.GetUserSalesOrganizations({
                        userKey: state.request.ownerKey,
                        companyId,
                        dbType
                    }),
                    { method: HttpMethods.GET }
                );
                const userSalesOrgs = await response.json();
                setState({ userSalesOrgs });
            }
            catch(e) {
                console.error(e);
                trackError(e);
            }
            finally {
                setIsLoadingSalesOrgs(false);
            }
        }
    };

    const getSalesOrgsOptions = (savedSalesOrganization) => {
        if (state.userSalesOrgs.length > 0){
            if (!!savedSalesOrganization && !state.userSalesOrgs.some(x => x.tableValueId === savedSalesOrganization.tableValueId)){
                const actualSOWithOldOne = [...state.userSalesOrgs].concat(savedSalesOrganization);
                return actualSOWithOldOne.map(x => ({ value: x.tableValueId, label: `${x.externalKey} - ${x.value}` }));
            }

            return state.userSalesOrgs.map(x => ({value: x.tableValueId, label: `${x.externalKey} - ${x.value}`}));
        }
        else if (savedSalesOrganization){
            return [savedSalesOrganization].map(x => ({value: x.tableValueId, label: `${x.externalKey} - ${x.value}`}));
        }
        else
            return [{ value: "", label: "No available values" }];
    }
    
    const [isLoadingCg7, setIsLoadingCg7] = useState(false);
    const getCustomerGroup7 = async () => {
        try {
            setIsLoadingCg7(true);

            const response = await fetchAuthorized(
                ApiRoutes.Request.GetCustomerGroups7(),
                { method: HttpMethods.GET }
            );

            const customerGroups7 = await response.json();
            setState({ customerGroups7 });
        }
        catch(e) {
            console.error(e);
            trackError(e);
        }
        finally {
            setIsLoadingCg7(false);
        }
    };

    const getCg7_Options = () => {
        if (state.customerGroups7.length > 0){
            return state.customerGroups7.map(x => ({
                value: x.tableValueId,
                label: `${x.externalKey} - ${x.value}`
            }));
        }
        else
            return [{ value: "", label: "No available values" }];
    };

    const [isLoadingCg8, setIsLoadingCg8] = useState(false);
    const getCustomerGroup8 = async (salesOrganizationId) => {
        if (salesOrganizationId){
            try {
                setIsLoadingCg8(true);

                const response = await fetchAuthorized(
                    ApiRoutes.Request.GetCustomerGroups8({ salesOrganizationId }),
                    { method: HttpMethods.GET }
                );

                const customerGroups8 = await response.json();
                setState({ customerGroups8 });
            }
            catch(e) {
                console.error(e);
                trackError(e);
            }
            finally {
                setIsLoadingCg8(false);
            }
        }
    };

    const getCg8_Options = () => {
        if (state.customerGroups8.length > 0){
            return state.customerGroups8.map(x => ({
                value: x.tableValueId,
                label: `${x.externalKey} - ${x.value}`
            }));
        }
        else
            return [{ value: "", label: "No available values" }];
    };

    const [isLoadingCg9, setIsLoadingCg9] = useState(false);
    const getCustomerGroup9 = async () => {
        try {
            setIsLoadingCg9(true);

            const response = await fetchAuthorized(
                ApiRoutes.Request.GetCustomerGroups9(),
                { method: HttpMethods.GET }
            );

            const customerGroups9 = await response.json();
            setState({ customerGroups9 });
        }
        catch(e) {
            console.error(e);
            trackError(e);
        }
        finally {
            setIsLoadingCg9(false);
        }
    };

    const getCg9_Options = () => {
        if (state.customerGroups9.length > 0){
            return state.customerGroups9.map(x => ({
                value: x.tableValueId,
                label: `${x.externalKey} - ${x.value}`
            }));
        }
        else
            return [{ value: "", label: "No available values" }];
    };

    const [isLoadingCg10, setIsLoadingCg10] = useState(false);
    const getCustomerGroup10 = async (responsibilityAreaId) => {
        if (responsibilityAreaId){
            try {
                setIsLoadingCg10(true);

                const response = await fetchAuthorized(
                    ApiRoutes.Request.GetCustomerGroups10({ responsibilityAreaId }),
                    { method: HttpMethods.GET }
                );

                const customerGroups10 = await response.json();
                setState({ customerGroups10 });
            }
            catch(e) {
                console.error(e);
                trackError(e);
            }
            finally {
                setIsLoadingCg10(false);
            }
        }
    };

    const getCg10_Options = () => {
        if (state.customerGroups10.length > 0){
            return state.customerGroups10.map(x => ({
                value: x.tableValueId,
                label: `${x.externalKey} - ${x.value}`
            }));
        }
        else
            return [{ value: "", label: "No available values" }];
    };

    /*const [isLoadingDistributionChannels, setIsLoadingDistributionChannels] = useState(false);
    const getDistributionChannels = async (customerGroup6Id) => {
        if (customerGroup6Id){
            try {
                setIsLoadingDistributionChannels(true);

                const response = await fetchAuthorized(
                    ApiRoutes.Request.GetDistributionChannels({ customerGroup6Id }),
                    { method: HttpMethods.GET }
                );

                const distributionChannels = await response.json();
                setState({ distributionChannels });
            }
            catch(e) {
                console.error(e);
                trackError(e);
            }
            finally {
                setIsLoadingDistributionChannels(false);
            }
        }
    };

    const getDistributionChannelsOptions = () => {
        if (state.distributionChannels.length > 0){
            return state.distributionChannels.map(x => ({
                value: x.tableValueId,
                label: `${x.externalKey} - ${x.value}`
            }));
        }
        else
            return [{ value: "", label: "No available values" }];
    };*/

    const [isLoadingInfoTypes, setIsLoadingInfoTypes] = useState(false);
    const getInfoTypes = async () => {
        try {
            setIsLoadingInfoTypes(true);

            const response = await fetchAuthorized(
                ApiRoutes.Request.GetInfoTypes(),
                { method: HttpMethods.GET }
            );

            const infoTypes = await response.json();
            setState({ infoTypes });
        }
        catch(e) {
            console.error(e);
            trackError(e);
        }
        finally {
            setIsLoadingInfoTypes(false); 
        }
    };

    const getSegments = async (salesOrganizationId, distributionChannelId) => {
        try {
            const response = await fetchAuthorized(
                ApiRoutes.Request.GetSegments({ salesOrganizationId, distributionChannelId }),
                { method: HttpMethods.GET }
            );

            const segments = await response.json();
            setState({ segments });
        }
        catch(e) {
            console.error(e);
            trackError(e);
        }
    };

    const getSegmentsOptions = () => {
        if (state.segments.length > 0){
            const creditControlAreas = getTableValues(SapTableNames.CreditControlAreas, DbType.S4);
            const salesOrganizations = getTableValues(SapTableNames.SalesOrganizations, DbType.S4);
            const distributionChannels = getTableValues(SapTableNames.DistributionChannels, DbType.S4);

            return state.segments.map(x => {
                const externalKeys: string[] = x.externalKey.split("_");
                const cca = creditControlAreas.find(y => y.externalKey === externalKeys[0]);
                const so = salesOrganizations.find(y => y.externalKey === externalKeys[1]);
                const dc = distributionChannels.find(y => y.externalKey === externalKeys[2]);

                const ccaLabel = cca ? `${cca.externalKey} - ${cca.value}` : "undefined";
                const soLabel = so ? `${so.externalKey} - ${so.value}` : "undefined";
                const dcLabel = dc ? `${dc.externalKey} - ${dc.value}` : "undefined";

                return ({
                    value: x.tableValueId,
                    label: `${ccaLabel} / ${soLabel} / ${dcLabel}`
                });
            });
        }
        else
            return [{ value: "", label: "No available values" }];
    };

    const [isLoadingBuCategories, setIsLoadingBuCategories] = useState(false);
    const getUserBusinessCategories = async (userKey, dbType) => {
        try {
            setIsLoadingBuCategories(true);
            const response = await fetchAuthorized(
                ApiRoutes.Request.GetUserBusinessCategories({ userKey, dbType }),
                { method: HttpMethods.GET }
            );
            const userBusinessCategories = await response.json();
            setState({ userBusinessCategories });
            setIsLoadingBuCategories(false);
            return([]);
        }
        catch(e) {
            console.error(e);
            trackError(e);
            setIsLoadingBuCategories(false);
            return([]);
        }        
    };

    const getUserBusinessCategoryOptions = (savedBusinessCategory) => {
        if(state.userBusinessCategories.length > 0) {
            if (!!savedBusinessCategory && !state.userBusinessCategories.some(x => x.tableValueId === savedBusinessCategory.tableValueId)){
                const actualBUWithOldOne = [...state.userBusinessCategories].concat(savedBusinessCategory);
                return actualBUWithOldOne.map(x => ({ value: x.tableValueId, label: x.value }));
            }

            return state.userBusinessCategories.map(x => ({ value: x.tableValueId, label: x.value }));
        }
        else if (savedBusinessCategory){
            return [savedBusinessCategory].map(x => ({value: x.tableValueId, label: x.value}));
        }
        else
            return [{ value: "", label: "No available values" }];
    }

    const [isLoadingTaxCategories, setIsLoadingTaxCategories] = useState(false);
    const getTaxCategories = async (salesOrgId, showObsolete, dbType) => {
        try {
            setIsLoadingTaxCategories(true);

            const response = await fetchAuthorized(
                ApiRoutes.Request.GetTaxCategories({ salesOrgId, showObsolete, dbType }),
                { method: HttpMethods.GET }
            );

            const taxCategories = await response.json();
            setState({ taxCategories });
        }
        catch(e) {
            console.error(e);
            trackError(e);
        }
        finally {
            setIsLoadingTaxCategories(false);
            return([]);
        }
    };
    
    //const [isLoadingAGMS4, setIsLoadingAGMS4] = useState(true);
    const getCustGroupsWithAccGroupS4 = async (salesOrgId, businessCategoryId) => {
        try {
            const response = await fetchAuthorized(
                ApiRoutes.Request.GetCustGroupsWithAccGroupS4({
                    salesOrgId,
                    businessCategoryId
                }),
                { method: HttpMethods.GET }
            );

            const custGroupsWithAccGroupS4 = await response.json();
            setState({ custGroupsWithAccGroupS4 });
        }
        catch(e) {
            console.error(e);
            trackError(e);
        }
        finally {
            //setIsLoadingAGMS4(false);
            return([]);
        }
    };

    const getFilteredCg6FromAGMS4 = (customerGroupId) => {
        if (customerGroupId !== "" && state.custGroupsWithAccGroupS4.length > 0){
            //making a distinct
            return state.custGroupsWithAccGroupS4
                .filter(x => x.customerGroup.tableValueId === customerGroupId)
                .map(x => ({
                    value: x.customerGroup6.tableValueId,
                    label: `${x.customerGroup6.externalKey} - ${x.customerGroup6.value}`
                }))
                .filter((item1, index, self) =>
                    index === self.findIndex(item2 =>
                        item2.value === item1.value &&
                        item2.label === item1.label
                    )
                );
        }
        else
            return [{ value: "", label: "No available values" }];
    };

    const getFilteredDistributionChannelFromAGMS4 = (customerGroup6Id) => {
        if (customerGroup6Id !== "" && state.custGroupsWithAccGroupS4.length > 0){
            //making a distinct
            return state.custGroupsWithAccGroupS4
                .filter(x => x.customerGroup6.tableValueId === customerGroup6Id)
                .map(x => ({
                    value: x.distributionChannel.tableValueId,
                    label: `${x.distributionChannel.externalKey} - ${x.distributionChannel.value}`
                }))
                .filter((item1, index, self) =>
                    index === self.findIndex(item2 =>
                        item2.value === item1.value &&
                        item2.label === item1.label
                    )
                );
        }
        else
            return [{ value: "", label: "No available values" }];
    };

    const getCustomerGroupOptionsS4 = () => {
        if (state.custGroupsWithAccGroupS4.length > 0){
            //making a distinct
            return state.custGroupsWithAccGroupS4
                .map(x => ({
                    value: x.customerGroup.tableValueId,
                    label: `${x.customerGroup.externalKey} - ${x.customerGroup.value}`
                }))
                .filter((item1, index, self) =>
                    index === self.findIndex(item2 =>
                        item2.value === item1.value &&
                        item2.label === item1.label
                    )
                );
        }
        else
            return [{ value: "", label: "No available values" }];
    };
    
    const getCustGroupsWithAccGroupECC = async (salesOrgId, businessCategoryId) => {
        try {
            const response = await fetchAuthorized(
                ApiRoutes.Request.GetCustGroupsWithAccGroupECC({
                    salesOrgId,
                    businessCategoryId
                }),
                { method: HttpMethods.GET }
            );

            const custGroupsWithAccGroupECC = await response.json();
            setState({ custGroupsWithAccGroupECC });
        }
        catch(e) {
            console.error(e);
            trackError(e);
        }
        finally {
            return([]);
        }
    };

    const getCustomerGroupOptionsECC = () => {
        if (state.custGroupsWithAccGroupECC.length > 0){
            return state.custGroupsWithAccGroupECC.map(x => ({
                value: x.customerGroup.tableValueId,
                label: `${x.customerGroup.externalKey} - ${x.customerGroup.value}`
            }));
        }
        else
            return [{ value: "", label: "No available values" }];
    };

    const [isLoadingRecoAccounts, setIsLoadingRecoAccounts] = useState(false);
    const getReconciliationAccounts = async (companyId, showObsolete, dbType) => {
        try {
            setIsLoadingRecoAccounts(true);

            const response = await fetchAuthorized(
                ApiRoutes.Request.GetReconciliationAccounts({ companyId, showObsolete, dbType }),
                { method: HttpMethods.GET }
            );

            const recoAccounts = await response.json();
            setState({ recoAccounts });
        }
        catch(e) {
            console.error(e);
            trackError(e);
        }
        finally {
            setIsLoadingRecoAccounts(false);
            return([]);
        }
    };

    const getReconciliationAccountOptions = () => {
        if (state.recoAccounts.length > 0){
            const reconciliationAccounts = state.recoAccounts;

            const sortByExternalKey = (a, b) => {
                if (a.externalKey < b.externalKey)
                  return -1;
                  
                if (a.externalKey > b.externalKey)
                  return 1;
                  
                return 0;
            };

            reconciliationAccounts.sort(sortByExternalKey);

            return reconciliationAccounts.map(x => ({ value: x.tableValueId, label: `${x.externalKey.split("_")[2]} - ${x.value}` }));
        }
        else
            return [{ value: "", label: "No available values" }];
    }

    const [isLoadingAccClerks, setIsLoadingAccClerks] = useState(false);
    const getAccountingClerks = async (companyId, prevSavedAccountingClerkId, showObsolete, dbType) => {
        try {
            setIsLoadingAccClerks(true);

            const response = await fetchAuthorized(
                ApiRoutes.Request.GetAccountingClerks({ companyId, prevSavedAccountingClerkId, showObsolete, dbType }),
                { method: HttpMethods.GET }
            );

            const accClerks = await response.json();
            setState({ accClerks });
        }
        catch(e) {
            console.error(e);
            trackError(e);
        }
        finally {
            setIsLoadingAccClerks(false);
            return([]);
        }
    };

    const getAccountingClerksOptions = () => {
        if(state.accClerks.length > 0) {
            const accClerks = state.accClerks.map(x => ({
                ...x,
                externalKey: x.externalKey.split("_")[1]
            }));

            const sortByExternalKey = (a, b) => {
                if (a.externalKey < b.externalKey)
                  return -1;
                  
                if (a.externalKey > b.externalKey)
                  return 1;
                  
                return 0;
            }

            accClerks.sort(sortByExternalKey);

            let options = accClerks.map(x => ({ value: x.tableValueId, label: `${x.externalKey} - ${x.value}` }));
            options = [ { value: "", label: "@None" }].concat(options)
            return options;
        } else {
            return [{ value: "", label: "No available values" }];
        }
    }

    const [isLoadingDelPlants, setIsLoadingDelPlants] = useState(false);
    const getDeliveringPlants = async (companyId, dbType) => {
        try {
            setIsLoadingDelPlants(true);

            const response = await fetchAuthorized(
                ApiRoutes.Request.GetDeliveringPlants({ companyId, dbType }),
                { method: HttpMethods.GET }
            );

            const deliveringPlants = await response.json();
            setState({ deliveringPlants });
        }
        catch(e) {
            console.error(e);
            trackError(e);
        }
        finally {
            setIsLoadingDelPlants(false);
            return([]);
        }
    }  

    const getDeliveringPlantsOptions = () => {
        if(state.deliveringPlants.length > 0) {
            const delPlants = state.deliveringPlants;
            const sortByExternalKey = (a, b) => {
                if (a.externalKey < b.externalKey)
                  return -1;
                  
                if (a.externalKey > b.externalKey)
                  return 1;
                  
                return 0;
            };

            delPlants.sort(sortByExternalKey);

            let options = delPlants.map(x => ({
                value: x.tableValueId,
                label: `${x.externalKey} - ${x.value}`
            }));

            options = [ { value: "", label: "@None" }].concat(options);
            
            return options;

        }
        else
            return [{ value: "", label: "No available values" }];
    }

    const getTransportationZoneOptions = (countryId, dbType) => {
        const countries = getTable(SapTableNames.Countries, state);

        if (!countries)
            return null;

        const childValues = dbType === DbType.ECC ?
            countries.childValues_ECC :
            countries.childValues_S4;

        const country = childValues.find(x => x.tableValueId === countryId);

        if (!country)
            return null;

        const values = getTableValues(SapTableNames.TransportationZones, dbType);

        if (values.length > 0) {
            return values
                .filter(x => x.externalKey.includes(country.externalKey))
                .map(x => ({
                    value: x.tableValueId,
                    label: `${x.externalKey} - ${x.value}`
                }));
        }
        else
            return [{ value: "", label: "No available values" }];
    };

    const getStateDataOptions = (data) => {
        if(data.length > 0) {
            return data.map(x => ({ value: x.tableValueId, label: x.value }));
        } else {
            return [{ value: "", label: "No available values" }];
        }
    }

    const validateSapCode = async (code, dbType) => {
        try {
            const _ = await fetchAuthorized(
                ApiRoutes.Sap.ValidateCode(),
                {
                    method: HttpMethods.POST,
                    body: JSON.stringify({ key: code, dbType })
                }
            );
                   
            return true;
        }
        catch(e) {
            console.error(e);
            trackError(e);
            return false;
        }
    };

    const clearError = (errorId) => {
        const newErrors = clonedeep(state.errors); 

        if(newErrors && newErrors.length > 0){
            const index = newErrors.findIndex(x => x.errorId === errorId);
            if(index !== -1){
                newErrors.splice(index, 1);
                setState({errors: newErrors})
            }
        }
    }

    const removeBankInfoErrors = () => {
        const newErrors = clonedeep(state.errors);
        if(newErrors && newErrors.length > 0){

            let index = newErrors.findIndex(x => x.errorId === "BankName");
            newErrors.splice(index, 1);

            index = newErrors.findIndex(x => x.errorId === "BankIBAN");
            newErrors.splice(index, 1);

            index = newErrors.findIndex(x => x.errorId === "SwiftBic");
            newErrors.splice(index, 1);

            setState({errors: newErrors});
        }
    }

    const getLogisitcUsers = async (customerRequestId, customerRequestStatus, dbType) => {
        try {
            setState({ loadingLogisticUsers: true });

            const response = await fetchAuthorized(
                ApiRoutes.User.GetInvolvedUsers({
                    customerRequestId,
                    onlyMissingUsers: customerRequestStatus === RequestStatus.Logistic,
                    dbType
                }),
                { method: HttpMethods.GET }
            );
            
            const logisticUsers = await response.json();
            setState({ logisticUsers, loadingLogisticUsers: false });
        }
        catch(e) {
            setState({ loadingLogisticUsers: false });
            console.error(e);
            trackError(e);
        }
    };

    const { Provider } = CustomerRequestContext;
    
    return (
        <Provider value={{
            state,
            isLoadingRequest,
            loadRequestDbType,
            loadRequest,
            getRequestSavedObsoleteDataECC,
            isSavingRequest,
            saveDraftRequest,            
            sendRequestToCreditRisk,
            sendRequestToLogistic,
            sendRequestToReview,
            sendRequestToCRE,
            sendRequestToARFromCR,
            reSendRequestToSap,
            rejectRequest,
            updateCreditRiskInfo,
            blockRequest,
            unblockRequest,
            closeRequest,
            updateSignedContractInfo,
            updateLogisticDocInfo,
            updateLogisticCuSrvInfo,
            updateLogisticPlanInfo,
            updateLogisticInfo,
            updatePricingInfo,
            updateAccountReceivableInfo,
            createEmptyRequest,
            resetRequestOwner,
            isLoadingDefs,            
            getChoosenTableDefinitions,
            getTable,
            getTableValues,
            getTableOptions,
            getTableOptionsWithCode,            
            getTableOptionsWithNumber,
            getStateOptions,
            isLoadingSalesGroup,
            getSalesGroups,            
            getSalesGroupOptions,
            isLoadingRespAreas,            
            getRespAreas,
            getRespAreasOptions,
            isLoadingCg7,
            getCustomerGroup7,
            getCg7_Options,
            isLoadingCg8,
            getCustomerGroup8,
            getCg8_Options,
            isLoadingCg9,
            getCustomerGroup9,
            getCg9_Options,
            isLoadingCg10,
            getCustomerGroup10,
            getCg10_Options,
            /*isLoadingDistributionChannels,
            getDistributionChannels,
            getDistributionChannelsOptions,*/
            getCustGroupsWithAccGroupS4,
            //isLoadingAGMS4,
            getFilteredCg6FromAGMS4,
            getFilteredDistributionChannelFromAGMS4,
            getCustomerGroupOptionsS4,
            getCustGroupsWithAccGroupECC,
            getCustomerGroupOptionsECC,
            getTaxCategories,
            isLoadingInfoTypes,
            getInfoTypes,
            getSegments,
            getSegmentsOptions,
            isLoadingSalesOffices,
            getSalesOffices,
            getSalesOfficeOptions,
            isLoadingCompanies,
            getUserCompanies,
            getUserCompanyOptions,
            getUserBusinessCategories,
            getUserBusinessCategoryOptions,
            isLoadingSalesOrgs,
            getSalesOrgsAsync,
            getSalesOrgsOptions,
            isLoadingBuCategories,
            isLoadingTaxCategories,
            isLoadingRecoAccounts,
            getReconciliationAccounts,
            getReconciliationAccountOptions,
            isLoadingAccClerks,
            getAccountingClerks,
            getAccountingClerksOptions,
            isLoadingDelPlants,
            getDeliveringPlants,
            getDeliveringPlantsOptions,
            getTransportationZoneOptions,
            getStateDataOptions,
            validateSapCode,
            clearError,
            removeBankInfoErrors,
            setIsBankInfoRequired,
            setHasAccountGroupDIST,
            setNoSendToSap,
            getLogisitcUsers
        }}>
            {props.children}
        </Provider>
    );
}